import { useState, useMemo } from 'react';
import { type CoffeeSubmission, statusLabel, statusColor, displayNames } from '../../lib/coffee-project';

type Filter = 'all' | 'happened' | 'waiting' | 'funny' | 'family' | 'old_friends';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'happened', label: 'Happened' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'funny', label: 'Funny' },
  { id: 'family', label: 'Family' },
  { id: 'old_friends', label: 'Old Friends' },
];

function matchesFilter(s: CoffeeSubmission, f: Filter): boolean {
  if (f === 'all') return true;
  if (f === 'happened') return s.status === 'happened';
  if (f === 'waiting') return s.status === 'waiting' || s.status === 'scheduled';
  if (f === 'family') return ['Parent', 'Brother / Sister', 'Grandparent', 'Kid'].includes(s.relationship);
  if (f === 'old_friends') return ['Friend', 'Former friend', 'Old coworker'].includes(s.relationship);
  if (f === 'funny') return s.reason ? /funny|stupid|ridiculous|absurd|hilarious/i.test(s.reason) : false;
  return true;
}

export default function LiveWall({ submissions }: { submissions: CoffeeSubmission[] }) {
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = useMemo(
    () => submissions.filter((s) => matchesFilter(s, filter)),
    [submissions, filter]
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-12 space-y-4">
        <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter">The Coffees</h2>
        <p className="text-white/50 text-xl">Some happened. Some haven't. Yet.</p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-12">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
              filter === f.id
                ? 'bg-[#fda36b] text-[#132947]'
                : 'bg-white/5 text-white/60 border border-white/10 hover:border-[#fda36b]/40 hover:text-white'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-white/40 text-lg">No coffees here yet. Be the first.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((s) => {
            const { label, isAnon } = displayNames(s);
            return (
              <div
                key={s.id}
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#fda36b]/30 transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-white/30 text-xs font-mono tracking-wider">
                    COFFEE #{String(s.project_number).padStart(3, '0')}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${statusColor(s.status)}`}>
                    {statusLabel(s.status)}
                  </span>
                </div>

                <h3 className={`text-xl font-bold mb-1 ${isAnon ? 'text-white/50' : 'text-white'}`}>
                  {label}
                </h3>
                <p className="text-white/50 text-sm mb-3">{s.relationship}</p>

                {s.time_since && (
                  <p className="text-white/40 text-xs mb-2">
                    {s.time_since === 'Way too long' ? "Way too long" : `${s.time_since} apart`}
                  </p>
                )}

                {s.reason && (
                  <p className="text-white/60 text-sm italic leading-relaxed mt-3 border-l-2 border-white/10 pl-3">
                    "{s.reason}"
                  </p>
                )}

                {s.status === 'happened' && s.featured_story && (
                  <p className="text-white/70 text-sm leading-relaxed mt-3">
                    {s.featured_story}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
